import { useState, useCallback, useEffect, useMemo } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Search,
  Clock,
  CheckCircle2,
  Package,
  User,
  Calendar,
  FileText,
  AlertCircle,
  TrendingUp,
  ShieldCheck,
  Send,
  PackageCheck,
  Eye,
  Truck,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  get_store_issue_list,
  get_store_issue_by_id,
  server_post_data,
  store_issue_items,
} from "../ServiceConnection/serviceconnection";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import Loader from "../components/layout/Loader";
import { useToast } from "../components/ui/use-toast";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

/**
 * Quantity ko readable banao — chhoti value pe zyada decimals.
 * 60 g -> 0.06 KG dikhna chahiye, "0.00" nahi.
 */
const fmtQty = (v) => {
  const n = num(v);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  let decimals = 2;
  if (abs < 0.001) decimals = 6;
  else if (abs < 0.01) decimals = 5;
  else if (abs < 1) decimals = 4;
  else if (abs < 100) decimals = 3;
  return String(parseFloat(n.toFixed(decimals)));
};

const fmtMoney = (v) => `₹${num(v).toFixed(2)}`;

const statusConfig = {
  0: {
    label: "Pending",
    color: "bg-warning/20 text-warning border-warning/30",
    icon: Clock,
  },
  1: {
    label: "Partial Completed",
    color: "bg-primary/20 text-primary border-primary/30",
    icon: TrendingUp,
  },
  2: {
    label: "Completed",
    color: "bg-success/20 text-success border-success/30",
    icon: CheckCircle2,
  },
};

const priorityConfig = {
  high: { label: "High", color: "bg-destructive/20 text-destructive" },
  medium: { label: "Medium", color: "bg-warning/20 text-warning" },
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
};

export default function StoreManager() {
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [indents, setindents] = useState([]);
  const [dashboard, setdashboard] = useState({});
  const [allUnits, setAllUnits] = useState({});
  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [showLoader, setshowLoader] = useState(false);

  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [verifyingIndent, setVerifyingIndent] = useState(null);
  const [isDispatching, setIsDispatching] = useState(false);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingIndent, setViewingIndent] = useState(null);

  // Kis department se maal nikalega (Main Store ya koi aur)
  const [sourceDeptId, setSourceDeptId] = useState(0);
  const [departments, setDepartments] = useState([]);

  /* ---------------- unit conversion ---------------- */

  // Base unit ka conversion_to_base_unit = 1 hota hai (0 nahi).
  // Purana code `=== 0` check karta tha, isliye conversion kabhi lagta hi nahi tha.
  const convFactor = useCallback(
    (unitId) => {
      const u = allUnits[unitId];
      const f = num(u?.conversion_to_base_unit, 1);
      return f > 0 ? f : 1;
    },
    [allUnits],
  );

  const toBase = useCallback(
    (qty, unitId) => num(qty) * convFactor(unitId),
    [convFactor],
  );

  const fromBase = useCallback(
    (baseQty, unitId) => num(baseQty) / convFactor(unitId),
    [convFactor],
  );

  const unitName = useCallback(
    (unitId) =>
      allUnits[unitId]?.short_code_name || allUnits[unitId]?.unit_name || "",
    [allUnits],
  );

  // Ek item ke liye kaunse units chunne diye jaayein — sirf same family
  const unitsForItem = useCallback(
    (item) => {
      const anchorId = item.unit_id || item.required_unit_id;
      const anchor = allUnits[anchorId];
      if (!anchor) return [];
      const parentId = Number(anchor.parent_unit_id) || Number(anchorId);
      return Object.values(allUnits)
        .filter((u) => Number(u.parent_unit_id) === parentId)
        .sort(
          (a, b) =>
            num(a.conversion_to_base_unit, 1) - num(b.conversion_to_base_unit, 1),
        );
    },
    [allUnits],
  );

  /* ---------------- data ---------------- */

  const master_data_get = async (status = "all", search = "") => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("status", status);
    fd.append("search", search);

    await server_post_data(get_store_issue_list, fd)
      .then((Response) => {
        if (Response.data.error) {
          handleError(Response.data.message);
        } else {
          setindents(Response.data.message.list || []);
          setdashboard(Response.data.message.dashboard || {});
          setDepartments(Response.data.message.departments || []);

          const unitsMap = (Response.data.message.units || []).reduce(
            (acc, unit) => {
              acc[unit.primary_id] = unit;
              return acc;
            },
            {},
          );
          setAllUnits(unitsMap);
        }
        setshowLoaderAdmin(false);
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  useEffect(() => {
    master_data_get();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Backend se aaye item ko UI ke liye taiyaar karo.
   * Sab kuch base unit me rakha jaata hai, display ke waqt convert hota hai.
   */
  const prepareItems = useCallback(
    (items = []) =>
      items.map((item) => {
        const displayUnitId = item.unit_id || item.required_unit_id;

        // Naya backend *_base bhejta hai. Purana nahi bhejta — fallback.
        const availableBase =
          item.available_qty_base !== undefined
            ? num(item.available_qty_base)
            : toBase(item.available_qty, displayUnitId);

        // {dept_id: balance_in_base} — department badalne pe dobara API nahi
        const availableByDept = item.available_by_dept || {};

        const requestedBase =
          item.requested_qty_base !== undefined
            ? num(item.requested_qty_base)
            : num(item.requested_qty_grams) ||
              toBase(item.required_qty, displayUnitId);

        const deliveredBase =
          item.delivered_qty_base !== undefined
            ? num(item.delivered_qty_base)
            : toBase(item.delivered_qty, displayUnitId);

        const pendingBase = Math.max(requestedBase - deliveredBase, 0);

        return {
          ...item,
          display_unit_id: displayUnitId,
          delivery_unit_id: displayUnitId, // default = indent ki unit
          received_qty: "",
          available_base: availableBase,
          available_by_dept: availableByDept,
          requested_base: requestedBase,
          delivered_base: deliveredBase,
          pending_base: pendingBase,
          rate_per_base: num(item.item_price),
        };
      }),
    [toBase],
  );

  const openIndent = async (indent, mode) => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("request_id", indent.indent_id);

    await server_post_data(get_store_issue_by_id, fd)
      .then((Response) => {
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        const data = Response.data.message.list?.[0];
        if (!data) {
          handleError("Indent not found");
          return;
        }

        if (mode === "verify") {
          setVerifyingIndent({ ...data, items: prepareItems(data.items) });
          setSourceDeptId(
            Number(data.default_source_department_id) ||
              Number(
                (data.departments || departments).find(
                  (d) => Number(d.is_main_store) === 1,
                )?.primary_id,
              ) ||
              0,
          );
          if (data.departments?.length) setDepartments(data.departments);
          setIsVerifyDialogOpen(true);
        } else {
          setViewingIndent(data);
          setIsViewDialogOpen(true);
        }
      })
      .catch(() => handleError("network"))
      .finally(() => setshowLoaderAdmin(false));
  };

  /* ---------------- row handlers ---------------- */

  const patchItem = (item_id, patch) => {
    setVerifyingIndent((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.item_id === item_id ? { ...it, ...patch } : it,
      ),
    }));
  };

  // Unit badla -> quantity ko nayi unit me convert karo (mitao mat)
  const handleUpdateUnit = (item_id, newUnitId) => {
    setVerifyingIndent((prev) => ({
      ...prev,
      items: prev.items.map((it) => {
        if (it.item_id !== item_id) return it;

        const oldUnitId = it.delivery_unit_id;
        const entered = num(it.received_qty);

        let converted = "";
        if (entered > 0) {
          const base = toBase(entered, oldUnitId);
          converted = String(parseFloat(fromBase(base, newUnitId).toFixed(6)));
        }

        return {
          ...it,
          delivery_unit_id: Number(newUnitId),
          received_qty: converted,
        };
      }),
    }));
  };

  const handleDeliveryQtyChange = (item_id, value) => {
    if (value !== "" && !/^\d*\.?\d{0,4}$/.test(value)) return;
    patchItem(item_id, { received_qty: value });
  };

  /* ---------------- per-row calculations ---------------- */

  const rowCalc = useCallback(
    (item) => {
      const unitId = item.delivery_unit_id;
      const entered = num(item.received_qty);
      const enteredBase = toBase(entered, unitId);

      // item_price backend se PER BASE UNIT aata hai (per gram / per ml).
      // Display unit ka rate = per-base rate x conversion factor.
      const ratePerDisplay = item.rate_per_base * convFactor(unitId);
      const lineTotal = enteredBase * item.rate_per_base;

      // Stock us department ka jo abhi source me chuna hai
      const byDept = item.available_by_dept || {};
      const availableBase = sourceDeptId
        ? num(byDept[String(sourceDeptId)], 0)
        : num(item.available_base);

      const availableInUnit = fromBase(availableBase, unitId);
      const pendingInUnit = fromBase(item.pending_base, unitId);

      const overStock = enteredBase > availableBase + 0.000001;
      const overPending =
        item.pending_base > 0 && enteredBase > item.pending_base + 0.000001;

      return {
        entered,
        enteredBase,
        ratePerDisplay,
        lineTotal,
        availableBase,
        availableInUnit,
        pendingInUnit,
        overStock,
        overPending,
        isFullyIssued: item.pending_base <= 0.000001,
        hasError: overStock || overPending,
      };
    },
    [toBase, fromBase, convFactor, sourceDeptId],
  );

  const sourceOptions = useMemo(
    () =>
      (departments || []).filter(
        (d) =>
          Number(d.primary_id) !==
          Number(verifyingIndent?.department_id || 0),
      ),
    [departments, verifyingIndent],
  );

  const sourceDeptName = useMemo(
    () =>
      (departments || []).find(
        (d) => Number(d.primary_id) === Number(sourceDeptId),
      )?.department_name || "store",
    [departments, sourceDeptId],
  );

  const grandTotal = useMemo(() => {
    if (!verifyingIndent?.items) return 0;
    return verifyingIndent.items.reduce(
      (sum, it) => sum + rowCalc(it).lineTotal,
      0,
    );
  }, [verifyingIndent, rowCalc]);

  /* ---------------- validation ---------------- */

  const validateDispatch = () => {
    if (!verifyingIndent?.items?.length) return false;

    if (!sourceDeptId) {
      toast({
        title: "Select a source",
        description: "Choose which department the stock comes from",
        variant: "destructive",
      });
      return false;
    }

    let anyQty = false;

    for (const item of verifyingIndent.items) {
      const c = rowCalc(item);

      if (c.entered <= 0) continue; // 0 = ye item skip, poora form nahi
      anyQty = true;

      if (c.overStock) {
        toast({
          title: "Not enough stock",
          description: `${item.item_name}: only ${fmtQty(
            c.availableInUnit,
          )} ${unitName(item.delivery_unit_id)} in ${sourceDeptName}`,
          variant: "destructive",
        });
        return false;
      }

      if (c.overPending) {
        toast({
          title: "Too much",
          description: `${item.item_name}: only ${fmtQty(
            c.pendingInUnit,
          )} ${unitName(item.delivery_unit_id)} is still pending`,
          variant: "destructive",
        });
        return false;
      }
    }

    // Purana code har 0 pe rok deta tha. Ab sirf tab rokte hain jab
    // SAARE items 0 hon — ek item 0 rakhna bilkul valid hai.
    if (!anyQty) {
      toast({
        title: "Nothing to dispatch",
        description: "Enter a quantity for at least one item",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  /* ---------------- dispatch ---------------- */

  const handleDispatch = async () => {
    if (!validateDispatch()) return;

    setIsDispatching(true);
    setshowLoader(true);

    const payloadItems = verifyingIndent.items
      .filter((it) => num(it.received_qty) > 0)
      .map((it) => {
        const c = rowCalc(it);
        return {
          item_id: it.item_id,
          item_name: it.item_name,
          received_qty: c.entered,
          delivery_unit_id: Number(it.delivery_unit_id),
          delivery_unit_name: unitName(it.delivery_unit_id),
          grams_unit_id: it.grams_unit_id,
          item_price_new: parseFloat(c.ratePerDisplay.toFixed(6)),
          total_price: parseFloat(c.lineTotal.toFixed(4)),
        };
      });

    console.log("store_issue_items payload:", payloadItems);

    const fd = new FormData();
    fd.append("request_id", verifyingIndent.indent_id);
    fd.append("source_department_id", String(sourceDeptId));
    fd.append("grand_amount", grandTotal.toFixed(2));
    fd.append("items", JSON.stringify(payloadItems));

    await server_post_data(store_issue_items, fd)
      .then((Response) => {
        if (Response.data.error) {
          handleError(Response.data.message);
        } else {
          handleSuccess(
            `Stock dispatched for ${verifyingIndent.indent_no}`,
          );
          setIsVerifyDialogOpen(false);
          setVerifyingIndent(null);
          master_data_get();
        }
      })
      .catch(() => handleError("network"))
      .finally(() => {
        setshowLoader(false);
        setIsDispatching(false);
      });
  };

  /* ---------------- list filter ---------------- */

  const filteredIndents = indents.filter((indent) => {
    const s = searchQuery.toLowerCase();
    const matchesSearch =
      s === "" ||
      (indent.indent_no || "").toString().toLowerCase().includes(s) ||
      (indent.item_name || "").toLowerCase().includes(s) ||
      (indent.department || "").toLowerCase().includes(s);

    const matchesStatus =
      statusFilter === "all" || Number(indent.status) === Number(statusFilter);

    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    const Icon = statusConfig[status]?.icon || Clock;
    return <Icon className="w-4 h-4" />;
  };

  /* ---------------- render ---------------- */

  return (
    <MainLayout>
      <Header
        title="Issue Material"
        subtitle="Verify and dispatch stock for department indents"
      />

      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.pending ?? 0}
                </p>
                <p className="text-sm text-muted-foreground">Pending Indents</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.partial ?? 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  Partial Completed
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.completed ?? 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  Completed Indents
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by indent no or item..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="0">Pending</SelectItem>
                <SelectItem value="1">Partial Completed</SelectItem>
                <SelectItem value="2">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {filteredIndents.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No stock indents found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">
                    Indent No
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground">Item</TableHead>
                  <TableHead className="text-muted-foreground">
                    Priority
                  </TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground">
                    Requested By
                  </TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIndents.map((indent) => (
                  <TableRow
                    key={indent.indent_id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="font-medium text-primary">
                      {indent.indent_no}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {indent.department}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-foreground">
                          {indent.item_name}
                        </p>
                        {indent.remarks && (
                          <p className="text-xs text-muted-foreground">
                            {indent.remarks}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-medium border-0",
                          priorityConfig[indent.priority?.toLowerCase()]
                            ?.color || "bg-muted",
                        )}
                      >
                        {priorityConfig[indent.priority?.toLowerCase()]?.label ||
                          indent.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-medium border gap-1",
                          statusConfig[indent.status]?.color,
                        )}
                      >
                        {getStatusIcon(indent.status)}
                        {statusConfig[indent.status]?.label || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {indent.requested_by}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(indent.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(Number(indent.status) === 1 ||
                          Number(indent.status) === 2) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openIndent(indent, "view")}
                            className="hover:bg-primary/10 hover:text-primary gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        )}

                        {Number(indent.status) === 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openIndent(indent, "verify")}
                            className="hover:bg-success/10 hover:text-success gap-2"
                          >
                            <ShieldCheck className="w-4 h-4" />
                            Verify
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* ---------------- View Dialog ---------------- */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[1000px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Material Issue Details</DialogTitle>
              <DialogDescription>
                Complete details of the material issue request
              </DialogDescription>
            </DialogHeader>

            {viewingIndent && (
              <div className="space-y-6 py-4">
                <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                      <FileText className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {viewingIndent.indent_no}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Indent ID: {viewingIndent.indent_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Badge
                      className={cn(
                        "font-medium border gap-1",
                        statusConfig[viewingIndent.status]?.color,
                      )}
                    >
                      {getStatusIcon(viewingIndent.status)}
                      {statusConfig[viewingIndent.status]?.label || "Unknown"}
                    </Badge>
                    <Badge
                      className={cn(
                        "font-medium border-0",
                        priorityConfig[viewingIndent.priority?.toLowerCase()]
                          ?.color || "bg-muted",
                      )}
                    >
                      {priorityConfig[viewingIndent.priority?.toLowerCase()]
                        ?.label || viewingIndent.priority}{" "}
                      Priority
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Department
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingIndent.department}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Requested By
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingIndent.requested_by}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Request Date
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {new Date(viewingIndent.date).toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>

                {viewingIndent.dispatch_stock_date && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg bg-success/5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
                          <Truck className="w-4 h-4 text-success" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Stock Transferred
                        </p>
                      </div>
                      <p className="text-base font-semibold text-foreground ml-10">
                        {viewingIndent.issued_from_department_name ? (
                          <>
                            {viewingIndent.issued_from_department_name}
                            <span className="mx-2 text-muted-foreground">
                              &rarr;
                            </span>
                            {viewingIndent.department}
                          </>
                        ) : (
                          "—"
                        )}
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg bg-success/5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-success" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Last Dispatch
                        </p>
                      </div>
                      <p className="text-base font-semibold text-foreground ml-10">
                        {new Date(
                          viewingIndent.dispatch_stock_date,
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <PackageCheck className="w-5 h-5" />
                    Material Details
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-muted-foreground">
                            Item Name
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Required
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Issued
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Pending
                          </TableHead>
                          <TableHead className="text-muted-foreground text-right">
                            Unit Price
                          </TableHead>
                          <TableHead className="text-muted-foreground text-right">
                            Total
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingIndent.items?.length > 0 ? (
                          viewingIndent.items.map((item, index) => {
                            const u = item.unit || unitName(item.unit_id);
                            const pending =
                              item.pending_qty !== undefined
                                ? num(item.pending_qty)
                                : Math.max(
                                    num(item.required_qty) -
                                      num(item.delivered_qty),
                                    0,
                                  );
                            return (
                              <TableRow key={index}>
                                <TableCell className="font-medium">
                                  {item.item_name}
                                </TableCell>

                                <TableCell className="text-center">
                                  <Badge variant="outline" className="font-semibold">
                                    {fmtQty(item.required_qty)} {u}
                                  </Badge>
                                </TableCell>

                                <TableCell className="text-center">
                                  <Badge
                                    className={cn(
                                      "font-semibold",
                                      pending <= 0
                                        ? "bg-success/20 text-success"
                                        : num(item.delivered_qty) > 0
                                          ? "bg-warning/20 text-warning"
                                          : "bg-muted text-muted-foreground",
                                    )}
                                  >
                                    {fmtQty(item.delivered_qty)} {u}
                                  </Badge>
                                </TableCell>

                                <TableCell className="text-center text-muted-foreground">
                                  {pending > 0 ? `${fmtQty(pending)} ${u}` : "—"}
                                </TableCell>

                                <TableCell className="text-right font-medium">
                                  {num(item.item_unit_price) > 0
                                    ? fmtMoney(item.item_unit_price)
                                    : "—"}
                                </TableCell>

                                <TableCell className="text-right font-semibold text-primary">
                                  {num(item.item_total_price) > 0
                                    ? fmtMoney(item.item_total_price)
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center text-muted-foreground"
                            >
                              No items found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex justify-end p-4 bg-muted/30 rounded-lg border">
                    <div className="flex justify-between gap-4 font-semibold text-lg min-w-64">
                      <span>Grand Total:</span>
                      <span className="text-primary">
                        {fmtMoney(viewingIndent.grand_amount)}
                      </span>
                    </div>
                  </div>
                </div>

                {viewingIndent.remarks && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Remarks
                    </h4>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <p className="text-sm text-foreground">
                        {viewingIndent.remarks}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ---------------- Verify & Dispatch ---------------- */}
        <Dialog open={isVerifyDialogOpen} onOpenChange={setIsVerifyDialogOpen}>
          <DialogContent className="sm:max-w-[1100px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Verify &amp; Dispatch Stock</DialogTitle>
              <DialogDescription>
                Enter what you are issuing. Leave an item at 0 to skip it.
              </DialogDescription>
            </DialogHeader>

            {verifyingIndent && (
              <div className="space-y-6 py-4">
                <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                      <FileText className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {verifyingIndent.indent_no}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Indent ID: {verifyingIndent.indent_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Badge
                      className={cn(
                        "font-medium border gap-1",
                        statusConfig[verifyingIndent.status]?.color,
                      )}
                    >
                      {getStatusIcon(verifyingIndent.status)}
                      {statusConfig[verifyingIndent.status]?.label || "Unknown"}
                    </Badge>
                    <Badge
                      className={cn(
                        "font-medium border-0",
                        priorityConfig[verifyingIndent.priority?.toLowerCase()]
                          ?.color || "bg-muted",
                      )}
                    >
                      {priorityConfig[verifyingIndent.priority?.toLowerCase()]
                        ?.label || verifyingIndent.priority}{" "}
                      Priority
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Department
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {verifyingIndent.department}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Requested By
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {verifyingIndent.requested_by}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Request Date
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {new Date(verifyingIndent.date).toLocaleDateString(
                        "en-US",
                        {
                          weekday: "short",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        },
                      )}
                    </p>
                  </div>
                </div>

                {/* Source department — kahan se maal nikalega */}
                <div className="p-4 border rounded-lg bg-primary/5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Truck className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Issue stock from
                      </p>
                    </div>

                    <Select
                      value={sourceDeptId ? String(sourceDeptId) : ""}
                      onValueChange={(v) => setSourceDeptId(Number(v))}
                    >
                      <SelectTrigger className="w-full sm:w-64">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {sourceOptions.map((d) => (
                          <SelectItem
                            key={d.primary_id}
                            value={String(d.primary_id)}
                          >
                            {d.department_name}
                            {Number(d.is_main_store) === 1 ? " (Main Store)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <p className="text-sm text-muted-foreground">
                      &rarr; <span className="font-medium text-foreground">
                        {verifyingIndent.department}
                      </span>
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Available quantities below update when you change this.
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <PackageCheck className="w-5 h-5" />
                    Stock Verification
                  </h4>

                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-muted-foreground">
                            Item Name
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Required
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Pending
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            In {sourceDeptName}
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Issue Qty
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Unit
                          </TableHead>
                          <TableHead className="text-muted-foreground text-right">
                            Unit Price
                          </TableHead>
                          <TableHead className="text-muted-foreground text-right">
                            Total
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {verifyingIndent.items?.length > 0 ? (
                          verifyingIndent.items.map((item, index) => {
                            const c = rowCalc(item);
                            const uName = unitName(item.delivery_unit_id);
                            const opts = unitsForItem(item);

                            return (
                              <TableRow
                                key={item.item_id ?? index}
                                className={c.hasError ? "bg-destructive/5" : ""}
                              >
                                <TableCell className="font-medium">
                                  <div className="flex flex-col gap-0">
                                    <span className="text-sm font-semibold text-foreground leading-tight">
                                      {item.item_alter_name ||
                                        item.item_name ||
                                        "—"}
                                    </span>
                                    {item.item_alter_name &&
                                      item.item_name &&
                                      item.item_alter_name.trim().toLowerCase() !==
                                        item.item_name.trim().toLowerCase() && (
                                        <span className="text-[11px] text-muted-foreground italic opacity-80 leading-none">
                                          ({item.item_name})
                                        </span>
                                      )}
                                  </div>
                                  {c.isFullyIssued && (
                                    <Badge className="mt-1 bg-success/20 text-success">
                                      Fully issued
                                    </Badge>
                                  )}
                                </TableCell>

                                <TableCell className="text-center">
                                  <Badge variant="outline" className="font-semibold">
                                    {fmtQty(
                                      fromBase(
                                        item.requested_base,
                                        item.delivery_unit_id,
                                      ),
                                    )}{" "}
                                    {uName}
                                  </Badge>
                                </TableCell>

                                <TableCell className="text-center text-muted-foreground">
                                  {c.pendingInUnit > 0
                                    ? `${fmtQty(c.pendingInUnit)} ${uName}`
                                    : "—"}
                                </TableCell>

                                <TableCell className="text-center">
                                  <Badge
                                    className={cn(
                                      "font-semibold",
                                      c.availableBase >= item.pending_base
                                        ? "bg-success/20 text-success"
                                        : c.availableBase > 0
                                          ? "bg-warning/20 text-warning"
                                          : "bg-destructive/20 text-destructive",
                                    )}
                                  >
                                    {fmtQty(c.availableInUnit)} {uName}
                                  </Badge>
                                </TableCell>

                                <TableCell className="text-center">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={item.received_qty}
                                    disabled={c.isFullyIssued}
                                    onChange={(e) =>
                                      handleDeliveryQtyChange(
                                        item.item_id,
                                        e.target.value,
                                      )
                                    }
                                    className={cn(
                                      "w-24 text-center mx-auto",
                                      c.hasError && "border-destructive",
                                    )}
                                  />
                                  {c.entered > 0 && (
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                      = {fmtQty(c.enteredBase)}{" "}
                                      {unitName(item.grams_unit_id)}
                                    </p>
                                  )}
                                  {c.overStock && (
                                    <p className="text-[11px] text-destructive mt-1">
                                      Only {fmtQty(c.availableInUnit)} in{" "}
                                      {sourceDeptName}
                                    </p>
                                  )}
                                  {!c.overStock && c.overPending && (
                                    <p className="text-[11px] text-destructive mt-1">
                                      Only {fmtQty(c.pendingInUnit)} pending
                                    </p>
                                  )}
                                </TableCell>

                                <TableCell className="text-center">
                                  <Select
                                    value={String(item.delivery_unit_id || "")}
                                    onValueChange={(v) =>
                                      handleUpdateUnit(item.item_id, v)
                                    }
                                    disabled={c.isFullyIssued}
                                  >
                                    <SelectTrigger className="w-[90px] mx-auto">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {opts.map((u) => (
                                        <SelectItem
                                          key={u.primary_id}
                                          value={String(u.primary_id)}
                                        >
                                          {u.short_code_name || u.unit_name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>

                                <TableCell className="text-right font-medium">
                                  {fmtMoney(c.ratePerDisplay)}
                                  <p className="text-[11px] text-muted-foreground">
                                    per {uName}
                                  </p>
                                </TableCell>

                                <TableCell className="text-right font-semibold">
                                  {fmtMoney(c.lineTotal)}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="text-center text-muted-foreground"
                            >
                              No items found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex justify-end p-4 bg-muted/30 rounded-lg border">
                    <div className="flex justify-between gap-4 font-semibold text-lg min-w-64">
                      <span>Grand Total:</span>
                      <span className="text-primary">{fmtMoney(grandTotal)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Prices are taken from the ingredient's average landed cost.
                    Change the unit and the rate adjusts with it.
                  </p>
                </div>

                {verifyingIndent.remarks && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Remarks
                    </h4>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <p className="text-sm text-foreground">
                        {verifyingIndent.remarks}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsVerifyDialogOpen(false)}
                disabled={isDispatching}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDispatch}
                disabled={isDispatching}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {isDispatching ? (
                  "Dispatching..."
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Dispatch Stock
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {(showLoaderAdmin || showLoader) && <Loader />}
    </MainLayout>
  );
}